import { useState, useEffect } from "react";
import { Button } from "@direct-campaign/ui";
import { apiFetch } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "canvasser";
  createdAt: string;
}

interface InviteForm {
  email: string;
  name: string;
  role: "admin" | "canvasser";
}

export function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteForm>({
    email: "",
    name: "",
    role: "canvasser",
  });
  const [inviteError, setInviteError] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState("");

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState<"admin" | "canvasser">("canvasser");
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // Delete confirm state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  async function loadUsers() {
    try {
      const res = await apiFetch("/api/users");
      if (res.ok) {
        const data = (await res.json()) as { users?: User[] };
        setUsers(data.users ?? []);
      }
    } catch {
      // silently fail — users list is non-critical
    } finally {
      setLoadingUsers(false);
    }
  }

  useEffect(() => {
    void loadUsers();
  }, []);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError("");
    setInviteSuccess("");
    setInviteLoading(true);

    const res = await apiFetch("/api/auth/invite", {
      method: "POST",
      body: JSON.stringify(inviteForm),
    });

    setInviteLoading(false);

    if (res.ok) {
      setInviteSuccess(`Invite sent to ${inviteForm.email}.`);
      setInviteForm({ email: "", name: "", role: "canvasser" });
      setShowInvite(false);
      void loadUsers();
    } else {
      const data = (await res.json()) as { error?: string };
      setInviteError(data.error ?? "Failed to send invite.");
    }
  }

  function startEdit(u: User) {
    setEditingId(u.id);
    setEditName(u.name);
    setEditRole(u.role);
    setEditError("");
    setDeletingId(null);
    setDeleteError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditError("");
  }

  async function handleEdit(e: React.FormEvent, id: string) {
    e.preventDefault();
    setEditError("");
    setEditLoading(true);

    const res = await apiFetch(`/api/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name: editName, role: editRole }),
    });

    setEditLoading(false);

    if (res.ok) {
      cancelEdit();
      void loadUsers();
    } else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setEditError(data.error ?? "Failed to update user.");
    }
  }

  async function handleDelete(id: string) {
    setDeleteError("");
    setDeleteLoading(true);

    const res = await apiFetch(`/api/users/${id}`, { method: "DELETE" });

    setDeleteLoading(false);

    if (res.ok) {
      setDeletingId(null);
      void loadUsers();
    } else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setDeleteError(data.error ?? "Failed to delete user.");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Users</h2>
          <p className="text-muted-foreground mt-1">
            Manage your team members and their roles.
          </p>
        </div>
        <Button onClick={() => setShowInvite(true)}>Invite user</Button>
      </div>

      {inviteSuccess && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 dark:bg-green-950/50 dark:border-green-900 dark:text-green-400">
          {inviteSuccess}
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <div className="rounded-lg border bg-card p-5 space-y-4 max-w-md">
          <h3 className="font-medium">Invite a new user</h3>
          <form onSubmit={(e) => void handleInvite(e)} className="space-y-3">
            {inviteError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                {inviteError}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Name</label>
              <input
                type="text"
                required
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Jane Smith"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                required
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="jane@example.com"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Role</label>
              <select
                value={inviteForm.role}
                onChange={(e) =>
                  setInviteForm({
                    ...inviteForm,
                    role: e.target.value as "admin" | "canvasser",
                  })
                }
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="canvasser">Canvasser</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={inviteLoading}>
                {inviteLoading ? "Sending…" : "Send invite"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowInvite(false);
                  setInviteError("");
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Users table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Role</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Joined</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingUsers ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No users yet. Invite your first team member.
                </td>
              </tr>
            ) : (
              users.map((u) =>
                editingId === u.id ? (
                  <tr key={u.id} className="border-b last:border-0 bg-muted/20">
                    <td colSpan={5} className="px-4 py-4">
                      <form
                        onSubmit={(e) => void handleEdit(e, u.id)}
                        className="space-y-3 max-w-md"
                      >
                        {editError && (
                          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                            {editError}
                          </div>
                        )}

                        <div className="flex gap-3">
                          <div className="space-y-1.5 flex-1">
                            <label className="text-sm font-medium">Name</label>
                            <input
                              type="text"
                              required
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            />
                          </div>
                          <div className="space-y-1.5 w-36 shrink-0">
                            <label className="text-sm font-medium">Role</label>
                            <select
                              value={editRole}
                              disabled={u.id === me?.id}
                              title={
                                u.id === me?.id ? "You can't change your own role" : undefined
                              }
                              onChange={(e) =>
                                setEditRole(e.target.value as "admin" | "canvasser")
                              }
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                            >
                              <option value="canvasser">Canvasser</option>
                              <option value="admin">Admin</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex gap-2 pt-1">
                          <Button type="submit" size="sm" disabled={editLoading}>
                            {editLoading ? "Saving…" : "Save"}
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={cancelEdit}>
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={u.id}
                    className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-3">
                      {u.name}
                      {u.id === me?.id && (
                        <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3 capitalize">{u.role}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {deletingId === u.id ? (
                        <div className="flex flex-col items-end gap-2">
                          {deleteError && (
                            <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-1.5 text-xs text-destructive">
                              {deleteError}
                            </div>
                          )}
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-xs text-muted-foreground">
                              Remove from campaign?
                            </span>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              disabled={deleteLoading}
                              onClick={() => void handleDelete(u.id)}
                            >
                              {deleteLoading ? "Deleting…" : "Confirm"}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDeletingId(null);
                                setDeleteError("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(u)}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={u.id === me?.id}
                            title={
                              u.id === me?.id ? "You can't delete your own account" : undefined
                            }
                            onClick={() => {
                              setDeletingId(u.id);
                              setDeleteError("");
                              cancelEdit();
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ),
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
